import { IconButton, Slide } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { DateConfigProvider } from '@superdispatch/dates';
import {
  Column,
  Columns,
  ExitTransitionPlaceholder,
  Stack,
  useResponsiveValue,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import loadCardImage from '../assets/load-card.png';
import { isAPIError } from 'shared/api/APIError';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { LocalStore, useLocalStore } from 'shared/helpers/Store';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import { PageHeader, PageLayout } from 'shared/layout/PageLayout';
import { PageToolbar } from 'shared/layout/PageToolbar';
import { isValidLoadForSuggestedLoadsBanner } from 'shared/modules/load-suggestions/SuggestedLoadsPerDriverBanner';
import { useOnboardingTourHandler } from 'shared/modules/onboarding/OnboardingIntercomTours';
import { ONBOARDING_FIRST_LOAD_TOUR_STORAGE_KEY } from 'shared/modules/onboarding/data/OnboardingConstants';
import { SuperPayYouGotPaidBanner } from 'shared/modules/superpay/SuperPayYouGotPaidBanner';
import { InstantTransferDialog } from 'shared/modules/superpay/instant-transfer/InstantTransferDialog';
import { InstantTransferInvitationBanner } from 'shared/modules/superpay/instant-transfer/InstantTransferInvitationDialog';
import { InstantPayBanner } from 'shared/modules/superpay/instant-transfer/InstantTransferPaymentBanner';
import { useDispatcherProfile } from 'shared/modules/dispatcher/DispatcherAPI';
import { ScrollToTop } from 'shared/routing/ScrollToTop';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import {
  LoadActionThreeModes,
  getLoadActionThreeModesVariants,
} from '../core/LoadActionThreeModes';
import { LoadAddTerminalDrawer } from '../core/LoadAddTerminalDrawer';
import { LoadAssign } from '../core/LoadAssign';
import {
  isLoadInstantActionType,
  useLoadInstantAction,
} from '../core/LoadInstantAction';
import { LoadMarkAsPaidDrawer } from '../core/LoadMarkAsPaidDrawer';
import { LoadNoteAddDialog } from '../core/LoadNoteAddDialog';
import { LoadReassignDialog } from '../core/LoadReassignDialog';
import { LoadSendBOLDrawer } from '../core/LoadSendBOLDrawer';
import { LoadSendInvoiceDrawer } from '../core/LoadSendInvoiceDrawer';
import { UpgradePlanDialog } from '../core/UpgradePlanDialog';
import {
  LoadDTO,
  LoadUpdateTerminalDTO,
  formatLoadStage,
  formatLoadStatus,
} from '../data/LoadDTO';
import { toLoadGroupByTab } from '../data/LoadGroupByOptionsDTO';
import {
  useLoadsCache,
  useLoadsPage,
  useLoadsPageCount,
} from '../data/LoadsAPI';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import {
  canChangeLoadsPageSize,
  DEFAULT_LOADS_PAGE_SIZE,
  getLoadsPageSize,
  LoadsPageParams,
} from '../data/LoadsPageParams';
import { LoadCancelDialog } from './LoadCancelDialog';
import { LoadsPageEmptyResult } from './LoadsPageEmptyResult';
import { LoadsPageFilter } from './LoadsPageFilter';
import { LoadsPageGroupByList } from './LoadsPageGroupByList';
import { LoadsPageHeaderActions } from './LoadsPageHeaderActions';
import { LoadsPageLoad } from './LoadsPageLoad';
import { LoadsPagePagination } from './LoadsPagePagination';
import { LoadsPageTabs } from './LoadsPageTabs';
import { LoadsPageLoadAction } from './data/LoadsActions';
import { useLoadsPageContext } from './data/LoadsPageParamsContext';
import { useSendInvoiceDrawerTour } from './data/useSendInvoiceDrawerTour';
import {
  getAnimationTargetTab,
  useLoadAnimation,
} from './data/useLoadAnimation';
import { VehicleSubhaulDrawer } from 'carrier/vehicles/core/VehicleSubhaulDrawer';

const AnimatedLoadCard = styled.img`
  z-index: 2;
  height: 80px;
  width: 220px;
  position: fixed;
`;

interface LoadWithBannerFlag {
  load: LoadDTO;
  shouldShowBanner: boolean;
}

function getLoadsWithBannerFlags(loads: LoadDTO[]): LoadWithBannerFlag[] {
  const shownDriverGuids = new Set<string>();
  return loads.map((load) => {
    const driverGuid = load.driver?.guid;
    const shouldShowBanner =
      isValidLoadForSuggestedLoadsBanner(load) &&
      !!driverGuid &&
      !shownDriverGuids.has(driverGuid);
    if (shouldShowBanner && driverGuid) {
      shownDriverGuids.add(driverGuid);
    }
    return { load, shouldShowBanner };
  });
}

export function LoadsPage() {
  useSendInvoiceDrawerTour();
  const [{ can_import, launch_create_import_loads_tour }, setQuery] =
    useQuery();
  const { addSnackbar } = useSnackbarStack();
  const { params, updateParams } = useLoadsPageContext();
  const [previousPageParams, setPreviousPageParams] =
    useState<LoadsPageParams>();
  const isMobile = useResponsiveValue(true, false, false);
  const loads = useLoadsPage(params);
  const loadsPageCount = useLoadsPageCount(
    params,
    loads.data?.pagination.count,
  );
  const { data: dispatcher } = useDispatcherProfile();
  const isPaginationEnabled = useFlag('carrier_loads_pagination');
  const pageSize = getLoadsPageSize(params);
  const canChangePageSize =
    isPaginationEnabled && canChangeLoadsPageSize(params.stage);
  const { data: settings } = useCarrierSettings();
  const { invalidateLoads, setLoadsPageItems } = useLoadsCache();

  const {
    isAnimating,
    triggerAnimation,
    animationStartPosition,
    animatedImageRef,
    loadCardRefs,
  } = useLoadAnimation();

  const groupListTab = toLoadGroupByTab(params.stage);
  const shouldShowGroupByList = !!params.order_by && !!groupListTab;

  const onboardingAssignDriverTourState = useLocalStore(
    ONBOARDING_FIRST_LOAD_TOUR_STORAGE_KEY,
  );

  const { startTour } = useOnboardingTourHandler({
    tourName: 'tour_create_or_import_load',
  });
  const { showLauncher, hideLauncher } = useCustomerSupport();
  useOnboardingTourHandler({
    tourName: 'tour_assign_driver',
    enabled:
      onboardingAssignDriverTourState === 'pending' && groupListTab === 'new',
    onSettled: () => {
      LocalStore.delete(ONBOARDING_FIRST_LOAD_TOUR_STORAGE_KEY);
    },
  });

  useEffect(() => {
    const loadsError = loads.error;
    /* Server returns error ObjectDoesNotExist when there are no loads on non-first page.
          For example: delete or move to another tab the last load on the page.
          In that case previous page should be opened. */
    if (isAPIError(loadsError)) {
      if (loadsError.type === 'ObjectDoesNotExist' && params.page > 1) {
        updateParams({ page: params.page - 1 });
      } else if (
        loadsError.message !== 'subscription_cancelled' &&
        loadsError.message !== 'trial_expired'
      ) {
        addSnackbar(loadsError.message, { variant: 'error' });
      }
    }
  }, [loads.error, addSnackbar, params.page, updateParams]);

  useEffect(() => {
    if (launch_create_import_loads_tour) {
      startTour();
      setQuery({ launch_create_import_loads_tour: undefined });
    }
  }, [launch_create_import_loads_tour, setQuery, startTour]);

  const [modalState, setModalState] = useState<
    undefined | { type: LoadsPageLoadAction; load?: LoadDTO }
  >();

  const getModalLoad = (variant: LoadsPageLoadAction): undefined | LoadDTO =>
    modalState?.type !== variant ? undefined : modalState.load;
  const getModalLoadGUID = (
    variant: LoadsPageLoadAction,
  ): undefined | string =>
    modalState?.type !== variant ? undefined : modalState.load?.guid;

  const closeModal = () => {
    setModalState(undefined);
  };

  const closeModalAndRefetch = () => {
    closeModal();
    invalidateLoads();
  };

  const mutateLoad = (guid: string, values: Partial<LoadDTO>) => {
    setLoadsPageItems(params, (items) =>
      items.map((x) => (x.guid !== guid ? x : { ...x, ...values })),
    );
  };

  const removeLoad = (guid: string) => {
    setLoadsPageItems(params, (items) => items.filter((x) => x.guid !== guid));
  };

  const { submitInstantAction, isSubmitting } = useLoadInstantAction({
    onActionComplete: (type, load) => {
      if (type === 'unarchive') {
        removeLoad(load.guid);
      }

      if (type === 'mark_as_unpaid' || type === 'move_to_vehicles') {
        invalidateLoads();
      }
    },
  });

  const isSendInvoiceDrawerOpened = getModalLoad('send_invoice_drawer');

  const shouldRestrictImport = !settings?.can_import_load;

  useEffect(() => {
    if (isSendInvoiceDrawerOpened) {
      hideLauncher();
    } else {
      showLauncher();
    }
  }, [isSendInvoiceDrawerOpened, showLauncher, hideLauncher]);

  useEffect(() => {
    if (can_import === 'disabled' && shouldRestrictImport) {
      setModalState(() => ({ type: 'upgrade_plan' }));
    } else {
      setModalState(undefined);
    }

    if (params.stage !== 'active') {
      setPreviousPageParams(params);
    }
  }, [params, can_import, shouldRestrictImport]);

  return (
    <>
      <DocumentTitle title={formatLoadStage(params.stage) + ' Loads'} />

      <ScrollToTop key={params.page} />

      <LoadNoteAddDialog
        onClose={closeModal}
        load={getModalLoad('add_note')}
        onSubmitSuccess={({ note, loadGUID }) => {
          closeModal();
          mutateLoad(loadGUID, { latest_internal_note: note });
        }}
      />

      <LoadReassignDialog
        onClose={closeModal}
        type={getModalLoad('reassign') ? 'reassign' : 'assign_trip'}
        load={getModalLoad('reassign') || getModalLoad('assign_trip')}
        onSubmit={() => {
          setModalState({
            type: 'assign',
            load: getModalLoad('reassign') || getModalLoad('assign_trip'),
          });
        }}
      />

      <LoadAssign
        onClose={closeModal}
        load={getModalLoad('assign')}
        onSubmitSuccess={({ loadGUID, currentDriver }) => {
          closeModal();
          if (currentDriver) {
            trackLoadsEvent({
              name: 'CTMS: Driver Reassigned',
              page: 'loads_list',
              utm_medium: formatLoadStage(params.stage),
            });
            removeLoad(loadGUID);
          } else {
            trackLoadsEvent({
              name: 'CTMS: Driver Assigned',
              page: 'loads_list',
              utm_medium: formatLoadStage(params.stage),
            });

            triggerAnimation(loadGUID, 'assigned');
            invalidateLoads();
          }
        }}
      />

      <InstantTransferInvitationBanner source="Load List" />

      <LoadSendBOLDrawer
        onClose={closeModal}
        pageType="loads_list"
        loadGUID={getModalLoadGUID('send_bol')}
        onSubmitSuccess={closeModalAndRefetch}
      />

      <LoadMarkAsPaidDrawer
        onClose={closeModal}
        load={getModalLoad('mark_as_paid')}
        onSubmitSuccess={() => {
          trackLoadsEvent({
            name: 'CTMS: Marked as Paid',
            page: 'loads_list',
            utm_medium: formatLoadStage(params.stage),
          });
          closeModalAndRefetch();
        }}
      />

      <LoadCancelDialog
        onClose={closeModalAndRefetch}
        load={getModalLoad('cancel')}
        disableAutoclose={true}
      />

      <UpgradePlanDialog
        onClose={closeModal}
        type="carrier_tms_pro"
        open={modalState?.type === 'upgrade_plan'}
        source="Load List"
      />

      {getLoadActionThreeModesVariants().map((variant) => (
        <LoadActionThreeModes
          key={variant}
          variant={variant}
          onClose={closeModal}
          load={getModalLoad(variant)}
          onSubmitSuccess={(data, { load }) => {
            closeModal();

            switch (variant) {
              case 'delete':
                trackLoadsEvent({
                  name: 'Carrier Deleted Load',
                  page: 'loads_list',
                  utm_medium: formatLoadStage(params.stage),
                  load_status: formatLoadStatus(load.status),
                  is_created_by_broker: !!load.is_created_by_broker,
                });
                break;
              case 'archive':
                trackLoadsEvent({
                  name: 'CTMS: Load Archived',
                  page: 'loads_list',
                  utm_medium: formatLoadStage(params.stage),
                });
                break;
              case 'restore':
                trackLoadsEvent({
                  name: 'CTMS: Restored Load',
                  page: 'loads_list',
                  utm_medium: formatLoadStage(params.stage),
                });
                break;

              case 'mark_as_picked_up':
                trackLoadsEvent({
                  name: 'CTMS: Marked as Picked Up',
                  page: 'loads_list',
                  utm_medium: formatLoadStage(params.stage),
                });
                break;

              case 'mark_as_delivered':
                trackLoadsEvent({
                  name: 'CTMS: Marked as Delivered',
                  page: 'loads_list',
                  utm_medium: formatLoadStage(params.stage),
                });
                break;

              case 'move_to_assigned':
                trackLoadsEvent({
                  name: 'CTMS: Moved to Assigned',
                  page: 'loads_list',
                  utm_medium: formatLoadStage(params.stage),
                });
                break;

              case 'move_to_unassigned':
                trackLoadsEvent({
                  name: 'CTMS: Moved to Unassigned',
                  page: 'loads_list',
                  utm_medium: formatLoadStage(params.stage),
                });
                break;

              case 'mark_as_new':
                trackLoadsEvent({
                  name: 'CTMS: Marked as New',
                  page: 'loads_list',
                  utm_medium: formatLoadStage(params.stage),
                });
                break;

              case 'dispatch_to_carrier':
                trackLoadsEvent({
                  name: 'CTMS: Dispatched To Carrier',
                  page: 'loads_list',
                  utm_medium: formatLoadStage(params.stage),
                });
                break;

              case 'unassign':
                trackLoadsEvent({
                  name: 'CTMS: Driver Unassigned',
                  page: 'loads_list',
                  utm_medium: formatLoadStage(params.stage),
                });
                break;
            }

            const targetTab = getAnimationTargetTab(variant);
            if (targetTab) {
              triggerAnimation(load.guid, targetTab);
            }

            if (
              variant === 'delete' ||
              variant === 'archive' ||
              variant === 'restore' ||
              variant === 'unassign' ||
              variant === 'mark_as_in_terminal'
            ) {
              removeLoad(load.guid);
            } else if (variant === 'remove_terminal') {
              const { terminals_diagram } = data as LoadUpdateTerminalDTO;

              mutateLoad(load.guid, { terminals_diagram });
            } else {
              invalidateLoads();
            }
          }}
        />
      ))}

      <VehicleSubhaulDrawer
        isOpen={!!getModalLoad('subhaul')}
        onClose={closeModal}
        load={getModalLoad('subhaul')}
      />

      <LoadSendInvoiceDrawer
        onClose={closeModal}
        loadGUID={getModalLoadGUID('send_invoice_drawer')}
        loadNumber={getModalLoad('send_invoice_drawer')?.number}
        isCreatedByBroker={
          getModalLoad('send_invoice_drawer')?.is_created_by_broker
        }
        stage={getModalLoad('send_invoice_drawer')?.stage}
        inspectionType={getModalLoad('send_invoice_drawer')?.inspection_type}
      />

      <InstantTransferDialog
        onClose={closeModalAndRefetch}
        open={modalState?.type === 'instant_transfer'}
        loadGUID={getModalLoadGUID('instant_transfer')}
        source="Load List"
      />

      <LoadAddTerminalDrawer
        onClose={closeModal}
        load={getModalLoad('add_terminal')}
        onSubmitSuccess={(response, { loadGUID }) => {
          trackLoadsEvent({
            name: 'CTMS: Terminal Added',
            page: 'loads_list',
            utm_medium: formatLoadStage(params.stage),
          });
          closeModal();
          mutateLoad(loadGUID, {
            terminals_diagram: response.terminals_diagram,
          });
        }}
      />
      <PageLayout
        loading={loads.isLoading || isSubmitting}
        header={
          <>
            <PageHeader
              title={params.stage === 'active' ? 'Search result' : 'Loads'}
              startAction={
                params.stage === 'active' && (
                  <IconButton
                    edge="start"
                    size="small"
                    aria-label="go back"
                    onClick={() => {
                      updateParams(previousPageParams || {}, {
                        strategy: 'reset',
                      });
                    }}
                  >
                    <ArrowBack />
                  </IconButton>
                )
              }
              endActions={
                <LoadsPageHeaderActions
                  onImportedLoads={() => {
                    if (params.stage === 'new') {
                      invalidateLoads();
                    } else {
                      updateParams({}, { strategy: 'reset' });
                    }
                  }}
                  onUpgradePlan={() => {
                    setModalState(() => ({ type: 'upgrade_plan' }));
                  }}
                />
              }
            />

            {!isMobile && (
              <PageToolbar>
                <LoadsPageFilter />
              </PageToolbar>
            )}

            {params.stage !== 'active' && <LoadsPageTabs />}
          </>
        }
      >
        <Stack space="small">
          <InstantPayBanner source="Load List" />
          {params.stage === 'paid' && (
            <SuperPayYouGotPaidBanner source="Load List Banner" />
          )}
          <Columns space="small">
            <Column width="fluid">
              <Stack space="xsmall">
                {loads.data?.data.length === 0 ? (
                  <>
                    <Box marginTop="xxlarge" />
                    <LoadsPageEmptyResult />
                  </>
                ) : (
                  getLoadsWithBannerFlags(loads.data?.data || []).map(
                    ({ load, shouldShowBanner }, index) => (
                      <Box
                        ref={(el: HTMLElement | null) => {
                          if (el) {
                            loadCardRefs.current.set(load.guid, el);
                          } else {
                            loadCardRefs.current.delete(load.guid);
                          }
                        }}
                        aria-label={`${
                          load.number ? load.number : 'Empty Number'
                        } Load Card #${index + 1}`}
                        key={load.guid}
                      >
                        <LoadsPageLoad
                          load={load}
                          showSuggestedLoadsBanner={shouldShowBanner}
                          onAction={(type) => {
                            if (isLoadInstantActionType(type)) {
                              submitInstantAction(type, load);
                            } else {
                              setModalState({ type, load });
                            }
                          }}
                        />
                      </Box>
                    ),
                  )
                )}
              </Stack>

              {loads.data != null &&
                loadsPageCount != null &&
                // The picker stays visible even when all loads fit on one page
                loadsPageCount > (canChangePageSize ? 0 : pageSize) && (
                  <LoadsPagePagination
                    currentPage={params.page}
                    count={loadsPageCount}
                    pageSize={pageSize}
                    onPageChange={(nextPage) => {
                      updateParams({ page: nextPage });
                    }}
                    onPageSizeChange={
                      canChangePageSize
                        ? (nextPageSize) => {
                            trackLoadsEvent({
                              name: 'loads_page_size_changed',
                              tab: params.stage,
                              old_size: pageSize,
                              new_size: nextPageSize,
                              user_role: dispatcher?.user?.roles?.[0] ?? null,
                            });
                            updateParams({
                              page: 1,
                              page_size:
                                nextPageSize === DEFAULT_LOADS_PAGE_SIZE
                                  ? null
                                  : nextPageSize,
                            });
                          }
                        : undefined
                    }
                  />
                )}
            </Column>
            <Slide
              direction="left"
              in={shouldShowGroupByList && !isMobile}
              mountOnEnter={true}
              unmountOnExit={true}
              timeout={300}
            >
              <Column width="content">
                <ExitTransitionPlaceholder in={shouldShowGroupByList}>
                  {shouldShowGroupByList &&
                    Boolean(groupListTab) &&
                    params.order_by && (
                      <DateConfigProvider format="DateISO">
                        <LoadsPageGroupByList
                          tab={groupListTab}
                          groupBy={params.order_by}
                        />
                      </DateConfigProvider>
                    )}
                </ExitTransitionPlaceholder>
              </Column>
            </Slide>
          </Columns>
        </Stack>
      </PageLayout>

      {isAnimating && animationStartPosition && !isMobile && (
        <AnimatedLoadCard
          ref={animatedImageRef}
          src={loadCardImage}
          alt="Load assignment animation"
          style={{
            left: animationStartPosition.x,
            top: animationStartPosition.y,
          }}
        />
      )}
    </>
  );
}
